import React, { useEffect, useRef } from "react";
import { useMediaUrlFallback } from "../hooks";
import { MEDIA_BACKGROUND_COLOR, MediaSlideProps } from "./shared";

export * from "./shared";

const baseMediaStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
  objectFit: "contain",
  display: "block",
};

export const MediaSlide = ({
  item,
  backgroundColor = MEDIA_BACKGROUND_COLOR,
  loop = true,
  paused = false,
  onEnd,
  onError,
}: MediaSlideProps) => {
  const { url, handleError } = useMediaUrlFallback(item);
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaStyle: React.CSSProperties = { ...baseMediaStyle, backgroundColor };

  /**
   * `paused` is a PROP, and `<video>` has no matching attribute — play state is imperative on
   * the DOM element, so it is driven here rather than declared in the markup.
   *
   * ⚠️ `play()` returns a promise that REJECTS when the browser blocks autoplay, and an
   * unhandled rejection in a kiosk that never closes is a console filling up all day. The clip
   * is muted, which is what browsers gate autoplay on, so this should not fire — the catch is
   * for the case where it does.
   */
  useEffect(() => {
    const element = videoRef.current;
    if (!element) return;
    if (paused) element.pause();
    else void element.play().catch(() => {});
  }, [paused]);

  if (item.type === "video") {
    return (
      <video
        ref={videoRef}
        key={item.id}
        src={item.url}
        poster={item.posterUrl}
        style={mediaStyle}
        autoPlay
        muted
        loop={loop}
        playsInline
        onEnded={onEnd}
        onError={onError}
      />
    );
  }

  return <img src={url} alt="" style={mediaStyle} onError={handleError} />;
};

export default MediaSlide;
